import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { dateParam, day, peopleLabel } from "../../utils/constants/listFilters";
import { formatDisplayDate } from "../../utils/helperFunctions";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import useFilterDraft from "../../hooks/useFilterDraft";
import DateRangeFilter from "../common/DateRangeFilter";
import ActiveFilterChips from "../common/ActiveFilterChips";
import RequestedByFilter from "../common/RequestedByFilter";
import FiltersDrawer, {
  FilterField,
  FilterSection,
} from "../common/FiltersDrawer";
import { Input } from "../ui/input";

const EMPTY_PANEL = {
  requestedBy: "",
  requestedByLabel: "",
  deliveredFrom: null,
  deliveredTo: null,
};

const range = (params, prefix) => ({
  from: dateParam(params, `${prefix}_from`),
  to: dateParam(params, `${prefix}_to`),
});

const handleRangeLabel = ({ from, to }) =>
  from && to
    ? `${formatDisplayDate(from)} – ${formatDisplayDate(to)}`
    : from
      ? `From ${formatDisplayDate(from)}`
      : `Until ${formatDisplayDate(to)}`;

/** Ad Launch filters: bar, drawer and chips, mirrored into the URL; reports API params via onChange. */
const LaunchFilterBar = ({
  statusActive,
  onClearStatus,
  onChange,
  actions,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("query") || ""
  );
  const [requested, setRequested] = useState(() =>
    range(searchParams, "requested")
  );
  const [launchDate, setLaunchDate] = useState(() =>
    range(searchParams, "launch")
  );
  const [panel, setPanel] = useState(() => ({
    ...EMPTY_PANEL,
    requestedBy: searchParams.get("requested_by") || "",
    deliveredFrom: dateParam(searchParams, "delivered_from"),
    deliveredTo: dateParam(searchParams, "delivered_to"),
  }));
  const searchQuery = useDebouncedValue(searchInput, 400);
  const { open, draft, setDraft, onOpenChange, apply } = useFilterDraft(
    panel,
    setPanel
  );

  const apiParams = {
    search: searchQuery.trim() || undefined,
    requested_date_from: day(requested.from) || undefined,
    requested_date_to: day(requested.to) || undefined,
    delivered_date_from: day(panel.deliveredFrom) || undefined,
    delivered_date_to: day(panel.deliveredTo) || undefined,
    launch_date_from: day(launchDate.from) || undefined,
    launch_date_to: day(launchDate.to) || undefined,
    requested_by_id: panel.requestedBy ? Number(panel.requestedBy) : undefined,
  };
  const filterKey = JSON.stringify(apiParams);

  useEffect(() => {
    onChange({ apiParams: JSON.parse(filterKey), key: filterKey });
  }, [filterKey, onChange]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const setOrDelete = (key, val) =>
          val ? next.set(key, val) : next.delete(key);
        setOrDelete("query", searchQuery.trim());
        setOrDelete("requested_from", day(requested.from));
        setOrDelete("requested_to", day(requested.to));
        setOrDelete("delivered_from", day(panel.deliveredFrom));
        setOrDelete("delivered_to", day(panel.deliveredTo));
        setOrDelete("launch_from", day(launchDate.from));
        setOrDelete("launch_to", day(launchDate.to));
        setOrDelete("requested_by", panel.requestedBy);
        return next;
      },
      { replace: true }
    );
  }, [searchQuery, requested, launchDate, panel, setSearchParams]);

  const chips = [
    panel.requestedBy && {
      key: "requested_by",
      label: `Requested by: ${panel.requestedByLabel || peopleLabel([], panel.requestedBy)}`,
      onRemove: () =>
        setPanel((prev) => ({
          ...prev,
          requestedBy: "",
          requestedByLabel: "",
        })),
    },
    (panel.deliveredFrom || panel.deliveredTo) && {
      key: "delivered",
      label: `Delivered: ${handleRangeLabel({ from: panel.deliveredFrom, to: panel.deliveredTo })}`,
      onRemove: () =>
        setPanel((prev) => ({
          ...prev,
          deliveredFrom: null,
          deliveredTo: null,
        })),
    },
  ].filter(Boolean);

  const hasAnyFilter = !!(
    chips.length ||
    searchInput ||
    requested.from ||
    requested.to ||
    launchDate.from ||
    launchDate.to ||
    statusActive
  );

  const clearAll = () => {
    setPanel(EMPTY_PANEL);
    setSearchInput("");
    setRequested({ from: null, to: null });
    setLaunchDate({ from: null, to: null });
    onClearStatus();
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search ad, adset, track…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="h-9 w-full border-gray-300 bg-white pl-8"
          />
        </div>
        <DateRangeFilter
          label="Requested Date"
          from={requested.from}
          to={requested.to}
          onChange={(from, to) => setRequested({ from, to })}
        />
        <DateRangeFilter
          label="Launch Date"
          upcoming
          from={launchDate.from}
          to={launchDate.to}
          onChange={(from, to) => setLaunchDate({ from, to })}
        />
        <FiltersDrawer
          count={chips.length}
          open={open}
          onOpenChange={onOpenChange}
          onApply={apply}
          onReset={() => setDraft(EMPTY_PANEL)}
        >
          <FilterSection title="People">
            <FilterField label="Requested by" wide>
              <RequestedByFilter
                value={draft.requestedBy}
                onChange={(id, label) =>
                  setDraft((prev) => ({
                    ...prev,
                    requestedBy: id,
                    requestedByLabel: label,
                  }))
                }
                placeholder="Anyone"
                fullWidth
              />
            </FilterField>
          </FilterSection>
          <FilterSection title="Dates">
            <FilterField label="Delivered date" wide>
              <DateRangeFilter
                label="Delivered Date"
                from={draft.deliveredFrom}
                to={draft.deliveredTo}
                onChange={(from, to) =>
                  setDraft((prev) => ({
                    ...prev,
                    deliveredFrom: from,
                    deliveredTo: to,
                  }))
                }
              />
            </FilterField>
          </FilterSection>
        </FiltersDrawer>
        {actions}
      </div>
      {hasAnyFilter && (
        <ActiveFilterChips chips={chips} onClearAll={clearAll} />
      )}
    </>
  );
};

export default LaunchFilterBar;
